import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Resource } from "../../core/models/wrappers/Resource";
import { useApiClient } from "../clients/ApiClient";

export interface StockOrdersConfig {
  stockOrdersDisabled: boolean;
}

export interface StockOrdersConfigRepository {
  useConfig(): Resource<StockOrdersConfig>;
  update(stockOrdersDisabled: boolean): Promise<void>;
}

const STOCK_ORDERS_CONFIG_KEY = "stock-orders-config";

export function useStockOrdersConfigRepository(): StockOrdersConfigRepository {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  function useConfig(): Resource<StockOrdersConfig> {
    return useQuery(
      [STOCK_ORDERS_CONFIG_KEY],
      async (): Promise<StockOrdersConfig> => {
        const dto = await apiClient.getStockOrdersConfig();
        return { stockOrdersDisabled: dto.stockOrdersDisabled ?? false };
      },
      {
        // Admin surface — always show the current server state on entry.
        staleTime: 0,
      },
    );
  }

  async function update(stockOrdersDisabled: boolean): Promise<void> {
    await apiClient.updateStockOrdersConfig(stockOrdersDisabled);
    await queryClient.invalidateQueries({
      queryKey: [STOCK_ORDERS_CONFIG_KEY],
    });
    // The order-confirmation dialog reads stockOrdersDisabled from
    // logos/options/all, so the admin's own session must refetch it to see
    // the change take effect without a full reload.
    await queryClient.invalidateQueries({ queryKey: ["logo-options"] });
  }

  return {
    useConfig,
    update,
  };
}
